import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, ArrowRight, Clock, Info } from 'lucide-react'
import { ResponsiveWrapper } from './layout'
import BottomActionBar from './BottomActionBar'
import TwoFactorGuideModal from './TwoFactorGuideModal'
import { useI18n } from '../i18n'

export default function TwoFactorMigrationGate({ userId, onDismiss }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [dismissing, setDismissing] = useState(false)
  const [showGuide, setShowGuide] = useState(false)

  const handleSetupNow = () => {
    navigate('/security', { state: { userId, fromMigration: true } })
  }

  const handleRemindLater = () => {
    setDismissing(true)
    sessionStorage.setItem('orella_2fa_dismissed', Date.now().toString())
    setTimeout(() => onDismiss(), 300)
  }

  return (
    <ResponsiveWrapper className="bg-white shadow-xl" bottomInset={false}>
      <div className={`screen-min-h bg-white px-6 md:px-10 pt-12 flex flex-col transition-opacity duration-300 ${dismissing ? 'opacity-0' : 'opacity-100'}`}>
        <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-20 h-20 bg-orella-50 rounded-full flex items-center justify-center mb-6">
          <Shield className="w-10 h-10 text-orella-500" />
        </div>

        <h1 className="text-2xl font-bold text-orella-700 mb-3 text-center">
          {t('twoFactor.securityUpgradeAvailable', 'Security upgrade available')}
        </h1>

        <p className="text-gray-500 text-center mb-4 max-w-sm leading-relaxed">
          We've upgraded to two-factor authentication for stronger protection of your health information. Your PIN will be replaced with a more secure verification method.
        </p>

        {/* 2FA Setup Guide link */}
        <button
          onClick={() => setShowGuide(true)}
          className="text-orella-500 font-semibold text-[13px] mb-6 inline-flex items-center gap-1 underline decoration-orella-100 underline-offset-[3px] hover:decoration-orella-500 transition-colors"
        >
          <Info size={14} />
          {t('twoFactor.howDoes2faSetupWork')}
        </button>

        <TwoFactorGuideModal isOpen={showGuide} onClose={() => setShowGuide(false)} theme="blue" />

        <div className="w-full max-w-sm space-y-4">
          <div className="bg-orella-50 rounded-xl p-4">
            <p className="text-sm text-orella-700 font-medium mb-2">{t('twoFactor.whatsChanging')}</p>
            <ul className="text-sm text-orella-600 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-orella-500 mt-0.5">•</span>
                {t('twoFactor.pinReplaced')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orella-500 mt-0.5">•</span>
                {t('twoFactor.chooseAppOrSms', 'Choose an authenticator app or SMS text codes')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orella-500 mt-0.5">•</span>
                You'll get backup codes in case you lose your phone
              </li>
            </ul>
          </div>
        </div>
        </div>

        {/* PROD-1194: Set up now (and Remind me later) are pinned at the bottom of the screen. */}
        <BottomActionBar className="-mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center">
        <div className="w-full max-w-sm space-y-2">
          <button
            onClick={handleSetupNow}
            className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full flex items-center justify-center gap-2 breathe-blue"
          >
            {t('twoFactor.setUpNow')}
            <ArrowRight size={18} />
          </button>

          <button
            onClick={handleRemindLater}
            className="w-full py-3 text-gray-500 text-sm flex items-center justify-center gap-1.5 hover:text-gray-700 transition-colors"
          >
            <Clock size={14} />
            {t('twoFactor.remindMeLater', 'Remind me later')}
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-2 text-center max-w-xs">
          {t('twoFactor.pinStillWorksForNow', 'You can continue using your PIN for now, but this upgrade will become required soon.')}
        </p>
        </BottomActionBar>
      </div>
    </ResponsiveWrapper>
  )
}
